import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import {
  validateNumber,
  validatePresence
} from 'ember-changeset-validations/validators';
import { BufferedChangeset } from 'ember-changeset/types';
import IntlService from 'ember-intl/services/intl';
import { TReorderStrategy } from 'volta/models/types/planning-settings';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

const Validations = {
  demandHorizon: [validatePresence(true), validateNumber({ positive: true })],
  isProtected: [],
  reorderStrategy: []
};

export interface IMrpSettingsSubmit {
  demandHorizon?: number;
  isProtected: boolean;
  reorderStrategy?: TReorderStrategy;
}

interface IArgs extends IMrpSettingsSubmit {
  lt?: number;
  disabled: boolean;
  onSubmit?: (settings: IMrpSettingsSubmit) => void;
}

export default class MrpSettingsEditor extends Component<IArgs> {
  // Services
  // ~~~~~

  @service declare intl: IntlService;

  // Properties
  // ~~~~~

  @tracked settings!: IMrpSettingsSubmit;
  @tracked changeset!: BufferedChangeset;

  // Lifecycle hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initChangeset();
  }

  // Actions
  // ~~~~

  @action
  initChangeset() {
    this.settings = {
      demandHorizon: this.args.demandHorizon,
      isProtected: this.args.isProtected === true,
      reorderStrategy: this.args.reorderStrategy
    };
    this.changeset = Changeset(this.settings, lookupValidator(Validations), Validations);
  }

  @action
  defaultToLt() {
    if (this.args.lt) {
      this.changeset.set('demandHorizon', this.args.lt);
      return this.onSubmit();
    }
  }

  @action
  onDemandHorizonChange(value: string) {
    const parsed = parseInt(value, undefined);
    this.changeset.set('demandHorizon', isNaN(parsed) ? null : parsed);
    return this.onSubmit();
  }

  @action
  toggleIsProtected() {
    this.changeset.set('isProtected', !this.changeset.get('isProtected'));
    return this.onSubmit();
  }

  @action
  onSubmit() {
    this.changeset.validate();

    // We keep the changeset data as is and send it back to the calling component
    // We don't assume here what the validation rules are up the chain
    const data: IMrpSettingsSubmit = {
      demandHorizon: this.changeset.get('demandHorizon'),
      isProtected: this.changeset.get('isProtected'),
      reorderStrategy: this.changeset.get('reorderStrategy')
    };

    if (this.changeset.isValid) {
      this.changeset.execute();
    }

    return this.args.onSubmit?.(data);
  }
}
